import { CheckIcon } from "@heroicons/react/24/outline";
import { Autocomplete, Box, Button, Tab, Tabs, TextField } from "@mui/material";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { InternalLinkTool, InternalLinkToolConfig } from "./InternalLinkTool";

export interface InternalLinkSelection {
    id: string;
    label: string;
}

interface InternalLinkToolProps {
    config?: InternalLinkToolConfig;
    tool: InternalLinkTool;
    onCreateInternalLink: (elementSlug: string) => void;
    onCreateExternalLink: (target: string) => void;
    onRemove: () => void;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ display: "flex", minWidth: 300, p: 1, pt: 2 }}>{children}</Box>}
        </div>
    );
}

const InternalLinkToolDropdown = ({
    config,
    tool,
    onCreateInternalLink,
    onCreateExternalLink,
    onRemove,
}: InternalLinkToolProps) => {
    const [list, setList] = useState<Array<InternalLinkSelection>>([]);
    const [active, setActive] = useState<string | undefined>();
    const [selectedLink, setSelection] = useState<InternalLinkSelection | null>(null);
    const [targetLink, setTargetLink] = useState<string>("");

    const [external, setExternal] = useState(0);

    const handleChange = (_: SyntheticEvent, newValue: number) => {
        setExternal(newValue);
    };

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!config?.elements) return;

        const elementList = config.elements.map((x) => ({ label: x.name, id: x.id }));
        setList(elementList);

        const activeElement = config.elements.find((x) => x.slug === tool.elementSlug);
        if (activeElement) {
            setActive(activeElement.id);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    const triggerAdd = () => {
        switch (external) {
            case 0:
                if (!selectedLink) return;

                if (config?.elements) {
                    const link = config.elements.find((x) => x.id === selectedLink.id);
                    if (link) {
                        onCreateInternalLink(link.slug);
                    }
                }

                return;

            case 1:
                if (targetLink) onCreateExternalLink(targetLink);
                return;
        }

        onRemove();
    };

    const onFocus = () => {
        if (active) {
            const container = document.getElementsByClassName("ce-inline-tool-input--showed");
            if (container.length > 1) container[0].classList.remove("ce-inline-tool-input--showed");

            const existing = list.find((x) => x.id === active);
            if (existing) setSelection(existing);
        }
    };

    return (
        <>
            <input
                id="focus-input"
                ref={inputRef}
                style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    border: 0,
                    background: "transparent",
                    outline: "none",
                }}
                onFocus={onFocus}
            />
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={external} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label="Internal Link" />
                    <Tab label="External Link" />
                </Tabs>
            </Box>

            <CustomTabPanel value={external} index={0}>
                <Autocomplete
                    fullWidth
                    options={list}
                    value={selectedLink}
                    onChange={(_, newValue: InternalLinkSelection | null) => setSelection(newValue)}
                    renderInput={(params) => <TextField {...params} label="Element" />}
                    size="small"
                />

                <Button size="small" onClick={() => triggerAdd()} autoFocus={true} sx={{ ml: 1 }}>
                    <CheckIcon className="h-4 w-4" />
                </Button>
            </CustomTabPanel>
            <CustomTabPanel value={external} index={1}>
                <TextField fullWidth value={targetLink} onChange={(e) => setTargetLink(e.target.value)} size="small" />

                <Button size="small" onClick={() => triggerAdd()} autoFocus={true} sx={{ ml: 1 }}>
                    <CheckIcon className="h-4 w-4" />
                </Button>
            </CustomTabPanel>
        </>
    );
};

export default InternalLinkToolDropdown;
